import React, { useState } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { Button } from '../../../ui/button';
import { Menu, Moon, Sun } from 'lucide-react';
import { ThemeProvider, useTheme } from './ThemeContext';

interface AdminLayoutProps {
    children: React.ReactNode;
}

function AdminLayoutContent({ children }: AdminLayoutProps) {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const { theme, toggleTheme, effectiveTheme } = useTheme();

    return (
        <div 
            className={`min-h-screen ${effectiveTheme === 'dark' ? 'admin-dark' : 'admin-light'}`}
            style={{
                // Force admin theme variables to only apply within this boundary
                backgroundColor: effectiveTheme === 'dark' ? '#0A0A0A' : '#FFFFFF',
                color: effectiveTheme === 'dark' ? '#FFFFFF' : '#0F172A',
                isolation: 'isolate' // Creates a new stacking context
            }}
        >
            {/* Mobile Header with Burger Menu */}
            <div className="lg:hidden fixed top-0 left-0 right-0 h-16 glass-strong border-b border-primary/30 z-40 flex items-center justify-between px-4">
                <div className="flex items-center">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsSidebarOpen(true)}
                        className="text-primary"
                    >
                        <Menu className="w-6 h-6" />
                    </Button>
                    <h1 className="ml-4 text-foreground">Admin Dashboard</h1>
                </div>
                <Button
                    onClick={toggleTheme}
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary/10"
                    title={theme === 'system' ? 'System Theme (Click to switch)' : (effectiveTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode')}
                >
                    {effectiveTheme === 'dark' ? (
                        <Sun className="w-5 h-5" />
                    ) : (
                        <Moon className="w-5 h-5" />
                    )}
                </Button>
            </div>

            {/* Sidebar */}
            <AdminSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            {/* Main Content */}
            <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
                {children}
            </main>
        </div>
    );
}

export function AdminLayout({ children }: AdminLayoutProps) {
    return (
        <ThemeProvider>
            <AdminLayoutContent>{children}</AdminLayoutContent>
        </ThemeProvider>
    );
}